import { useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { Eye, EyeClosed } from "lucide-react";

export default function VendorSecurity() {
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [saving, setSaving] = useState(false);

  const updateField = (field, value) =>
    setPasswords({ ...passwords, [field]: value });

  const changePassword = async () => {
    if (!passwords.current.trim()) {
      return alert("Current password is required");
    }

    if (passwords.new.length < 8) {
      return alert("New password must be at least 8 characters");
    }

    if (passwords.new === passwords.current) {
      return alert("New password must be different from current password");
    }

    if (passwords.new !== passwords.confirm) {
      return alert("Passwords do not match");
    }

    setSaving(true);
    try {
    const res = await fetch(
      "/api/vendor/security/change-password",
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.new,
        }),
      },
    );

    let data;

    try {
      data = await res.json();
    } catch {
      return alert("Server error");
    }

    if (!res.ok) {
      return alert(data.message || "Unable to change password");
    }

    if (data.status === "success") {
      alert(data.message);
      setPasswords({
        current: "",
        new: "",
        confirm: "",
      });
    } else {
      alert(data.message || "Something went wrong");
    }
    } catch {
      alert('Unable to connect. Please try again.');
    } finally {setSaving(false);}
  };

  const logoutAll = async () => {
    if (!window.confirm("Logout from all devices?")) return;
    try {
    const res = await fetch(
      "/api/vendor/security/logout-all",
      {
        method: "POST",
        credentials: "include",
      },
    );

    const data = await res.json();

    if (data.status === "success") {
      alert("Logged out from all devices");
      window.location.href = "/vendorlogin";
    } else {
      alert(data.message);
    }
    } catch {alert('Unable to log out. Please try again.');}
  };

  return (
    <DashboardLayout>
      <div className="space-y-10">
        <div>
          <h1 className="text-2xl font-serif">Security</h1>
          <p className="text-sm text-gray-400">
            Manage your account security & login access
          </p>
        </div>

        <Section title="Change Password">
          <Input
            label="Current Password"
            value={passwords.current}
            onChange={(e) => updateField("current", e.target.value)}
            show={showPassword.current}
            toggle={() =>
              setShowPassword({
                ...showPassword,
                current: !showPassword.current,
              })
            }
          />

          <Input
            label="New Password"
            value={passwords.new}
            onChange={(e) => updateField("new", e.target.value)}
            show={showPassword.new}
            toggle={() =>
              setShowPassword({
                ...showPassword,
                new: !showPassword.new,
              })
            }
          />

          <Input
            label="Confirm Password"
            value={passwords.confirm}
            onChange={(e) => updateField("confirm", e.target.value)}
            show={showPassword.confirm}
            toggle={() =>
              setShowPassword({
                ...showPassword,
                confirm: !showPassword.confirm,
              })
            }
          />
          <div className="flex justify-end">
            <button
              onClick={changePassword}
              disabled={saving}
              className={`px-6 py-2 rounded-xl font-medium transition
                ${
                  saving
                    ? "bg-gray-500/40 text-gray-300"
                    : "bg-amber-400 text-black hover:bg-amber-300"
                }`}
            >
              {saving ? "Updating..." : "Update Password"}
            </button>
          </div>
        </Section>

        <Section title="Login Sessions">
          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <p className="text-sm">Current session is active.</p>

            <p className="text-xs text-gray-400 mt-1">
              Use "Logout from all devices" if you suspect unauthorized access.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={logoutAll}
              className="px-6 py-2 rounded-xl border border-red-500/40 text-red-400 hover:bg-red-500/10"
            >
              Logout from all devices
            </button>
          </div>
        </Section>

        <Section title="Two-Factor Authentication">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-medium">2FA Protection</p>
              <p className="text-xs text-gray-400">
                Add an extra layer of security to your account
              </p>
            </div>

            <span className="px-3 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">
              Coming Soon
            </span>
          </div>
        </Section>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-4">
      <h3 className="font-serif text-lg">{title}</h3>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, show, toggle }) {
  return (
    <div>
      <label className="text-xs text-gray-400">{label}</label>

      <div className="relative mt-1">
        <input
          aria-label={label}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          className="w-full px-4 py-2 pr-12 rounded-lg bg-black/40 border border-white/10"
        />

        <button
          type="button"
          onClick={toggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
        >
          {show ? <EyeClosed/> : <Eye/>}
        </button>
      </div>
    </div>
  );
}
